import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { 
  MessageSquare, Send, Users, Lock, Shield, Search, Smile, Image as ImageIcon, 
  X, Clock, Circle, Pin, PinOff, Bot, Sparkles, Check, CheckCheck, ChevronDown, 
  Phone, Video, MoreHorizontal, Hash, Volume2, PhoneCall, UserPlus, Settings,
  Bell, BellOff, Trash2, Flag, Reply, Forward, Copy, Star, Archive, Filter,
  ChevronRight, Plus, Crown, Edit2, LogOut, UserMinus, AtSign, Paperclip,
  FileText, Mic, StopCircle, MoreVertical, ThumbsUp, Heart, Laugh, AlertCircle,
  Zap, MessageCircle, Inbox, ChevronLeft, RefreshCw, Bookmark, ArrowRight,
  UserCheck, Building2, Megaphone, Coffee, Briefcase, Headphones, Pencil,
  Mail, HelpCircle, ClipboardList, RotateCcw, Wand2, Brain, BookOpen, Clipboard
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUtils';
import { CallContext } from '../../components/calling/CallProvider';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import StaffProfileCard from '../../components/StaffProfileCard';
import { ReactionPicker, ReactionDisplay } from '../../components/chat/ChatReactions';
import { VoiceRecorderButton, VoiceRecorderBar, VoiceMessagePlayer } from '../../components/chat/ChatVoiceRecorder';
import ChatBookmarksPanel from '../../components/chat/ChatBookmarksPanel';
import ChatChannelPanel from '../../components/chat/ChatChannelPanel';
import ChatForwardModal from '../../components/chat/ChatForwardModal';
import ChatMembersPanel, { ROLE_BADGES } from '../../components/chat/ChatMembersPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMOJI_LIST = ['😀', '😂', '😍', '🥰', '😎', '🤔', '👍', '👎', '❤️', '🔥', '✅', '⭐', '🎉', '💯', '🙏', '👋', '😊', '🤝', '💪', '🚀'];
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

const NOVA_SUGGESTIONS = [
  { label: 'Draft an email', prompt: 'Help me draft a professional email to', icon: Mail, color: 'cyan' },
  { label: 'Leave policy', prompt: 'What is the company leave policy and how do I request time off?', icon: BookOpen, color: 'emerald' },
  { label: 'Handle a complaint', prompt: 'How should I respond to an upset customer who', icon: HelpCircle, color: 'amber' },
  { label: 'Write an announcement', prompt: 'Help me write a professional announcement about', icon: Megaphone, color: 'purple' },
  { label: 'Shift questions', prompt: 'Can you explain how shifts work and how to swap a shift?', icon: Clock, color: 'blue' },
  { label: 'Improve my writing', prompt: 'Can you help me improve this text: ', icon: Wand2, color: 'pink' },
];

// Markdown components for Nova AI responses
const MarkdownComponents = {
  p: ({ children }) => <p className="text-white/90 text-sm leading-relaxed mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-white/80 italic">{children}</em>,
  h1: ({ children }) => <h1 className="text-white font-bold text-base mt-3 mb-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-white font-bold text-sm mt-3 mb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-white font-semibold text-sm mt-2 mb-1">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-2 ml-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-2 ml-1">{children}</ol>,
  li: ({ children }) => <li className="text-white/90 text-sm">{children}</li>,
  code: ({ inline, children }) => inline 
    ? <code className="bg-white/10 text-cyan-300 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    : <pre className="bg-black/30 border border-white/10 rounded-lg p-3 my-2 overflow-x-auto"><code className="text-cyan-300 text-xs font-mono">{children}</code></pre>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-purple-400/50 pl-3 my-2 text-white/70 italic">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">{children}</a>,
  hr: () => <hr className="border-white/10 my-3" />,
};

// Team Channels
const TEAM_CHANNELS = [
  { id: 'general', name: 'General', icon: Hash, badge: 'All Staff', color: 'cyan', desc: 'Company-wide discussions' },
  { id: 'operations', name: 'Operations', icon: Settings, badge: 'Ops', color: 'orange', desc: 'Operations team channel' },
  { id: 'customer-relations', name: 'Customer Relations', icon: Headphones, badge: 'CRE', color: 'green', desc: 'Customer relations team' },
  { id: 'managers', name: 'Management', icon: Shield, badge: 'Mgmt', color: 'purple', desc: 'Management only', restricted: true },
  { id: 'announcements', name: 'Announcements', icon: Megaphone, badge: 'Official', color: 'amber', desc: 'Official announcements', readOnly: true },
];

// Call buttons component
const CallButtons = ({ username, displayName }) => {
  const callContext = useContext(CallContext);
  
  if (!callContext) {
    return (
      <div className="flex items-center gap-1">
        <button className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/20 transition-colors">
          <Phone className="w-4 h-4" />
        </button>
        <button className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center hover:bg-cyan-500/20 transition-colors">
          <Video className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const { initiateCall, callStatus } = callContext;
  const isInCall = callStatus !== 'idle';

  return (
    <div className="flex items-center gap-1">
      <button 
        onClick={() => initiateCall(username, 'audio')}
        disabled={isInCall}
        className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
      >
        <Phone className="w-4 h-4" />
      </button>
      <button 
        onClick={() => initiateCall(username, 'video')}
        disabled={isInCall}
        className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center hover:bg-cyan-500/20 transition-colors disabled:opacity-50"
      >
        <Video className="w-4 h-4" />
      </button>
    </div>
  );
};

const StaffChatPageRevamped = ({ getAuthHeaders, currentUser }) => {
  // Core State
  const [pendingDM] = useState(() => sessionStorage.getItem('staff_chat_open_dm'));
  useEffect(() => { sessionStorage.removeItem('staff_chat_open_dm'); }, []);
  const [activeChat, setActiveChat] = useState(pendingDM || 'general');
  const [activeChatType, setActiveChatType] = useState(pendingDM ? 'dm' : 'channel');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [onlineStatus, setOnlineStatus] = useState({});
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sidebarView, setSidebarView] = useState(pendingDM ? 'direct' : 'channels'); // 'channels', 'direct', 'groups'
  const [showUserProfile, setShowUserProfile] = useState(null);
  const [profileCardTarget, setProfileCardTarget] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  
  // Advanced features
  const [pinnedUsers, setPinnedUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  
  // Edit / Delete / Typing
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [iAmTyping, setIAmTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [failedAvatars, setFailedAvatars] = useState({});
  
  // Modals
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showGroupSettingsModal, setShowGroupSettingsModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  
  // New features
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [showChannelPanel, setShowChannelPanel] = useState(false);
  const [channelInfo, setChannelInfo] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [forwardingMessage, setForwardingMessage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [showMembers, setShowMembers] = useState(true);
  const [groupAvatarFile, setGroupAvatarFile] = useState(null);
  const [groupAvatarPreview, setGroupAvatarPreview] = useState(null);
  const groupAvatarInputRef = useRef(null);
  
  // AI Assistant
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const isManager = ['manager', 'executive', 'superadmin', 'admin'].includes(currentUser?.role);

  // Initialize
  useEffect(() => {
    const saved = localStorage.getItem('staffchat_pinned');
    if (saved) setPinnedUsers(JSON.parse(saved));
    
    const aiHistory = localStorage.getItem('staffchat_ai_history');
    if (aiHistory) setAiMessages(JSON.parse(aiHistory));
    
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchStaffList();
    if (activeChatType !== 'ai') {
      if (activeChatType === 'group') {
        fetchGroupMessages(activeChat);
      } else {
        fetchMessages(activeChat);
      }
    }
    fetchOnlineStatus();
    
    // Fetch channel-specific data
    if (activeChatType === 'channel') {
      fetchChannelInfo(activeChat);
      fetchPinnedMessages(activeChat);
    }
    
    const interval = setInterval(() => {
      if (activeChatType !== 'ai') {
        if (activeChatType === 'group') {
          fetchGroupMessages(activeChat);
        } else {
          fetchMessages(activeChat);
        }
        fetchTypingStatus(activeChat);
      }
      fetchUnreadCounts();
      fetchOnlineStatus();
    }, 8000);
    
    return () => clearInterval(interval);
  }, [activeChat, activeChatType]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, aiMessages]);

  // Handle @ mentions
  useEffect(() => {
    const lastAtIndex = inputMessage.lastIndexOf('@');
    if (lastAtIndex !== -1 && lastAtIndex === inputMessage.length - 1) {
      setShowMentions(true);
      setMentionFilter('');
    } else if (lastAtIndex !== -1) {
      const textAfterAt = inputMessage.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setShowMentions(true);
        setMentionFilter(textAfterAt.toLowerCase());
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  }, [inputMessage]);

  // API Functions
  const fetchStaffList = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/staff-list`, { headers });
      setStaffList(response.data.filter(s => s.username !== currentUser?.username));
    } catch (error) {}
  };

  const fetchGroups = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/groups`, { headers });
      setGroups(response.data || []);
    } catch (error) {
      setGroups([]);
    }
  };

  const fetchOnlineStatus = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/online-status`, { headers });
      setOnlineStatus(response.data || {});
    } catch (error) {}
  };

  const fetchMessages = async (chatId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/messages/${chatId}`, { headers });
      setMessages(response.data);
    } catch (error) {}
  };

  const fetchGroupMessages = async (groupId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/groups/${groupId}/messages`, { headers });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching group messages:', error);
      setMessages([]);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/groups/${groupId}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching group details:', error);
      return null;
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/unread-counts`, { headers });
      setUnreadCounts(response.data || {});
    } catch (error) {}
  };

  const fetchTypingStatus = async (chatId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/typing-status/${chatId}`, { headers });
      setTypingUsers(response.data.typers || []);
    } catch { setTypingUsers([]); }
  };

  const handleTypingInput = useCallback((e) => {
    setInputMessage(e.target.value);
    if (activeChatType === 'ai') return;
    const headers = getAuthHeaders();
    if (!headers) return;

    if (!iAmTyping) {
      setIAmTyping(true);
      axios.post(`${BACKEND_URL}/api/staff-chat/typing/${activeChat}`, {}, { headers }).catch(() => {});
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setIAmTyping(false);
      axios.delete(`${BACKEND_URL}/api/staff-chat/typing/${activeChat}`, { headers }).catch(() => {});
    }, 3000);
  }, [activeChat, activeChatType, iAmTyping, getAuthHeaders]);

  useEffect(() => {
    return () => { if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current); };
  }, []);

  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && !selectedImage)) return;

    if (activeChatType === 'ai') {
      await handleAIMessage();
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      let imageUrl = null;
      if (selectedImage) {
        const formData = new FormData();
        formData.append('image', selectedImage);
        const uploadRes = await axios.post(`${BACKEND_URL}/api/staff-chat/upload-image`, formData, {
          headers: { Authorization: headers.Authorization }
        });
        imageUrl = uploadRes.data.image_url;
      }

      // Different endpoint for group messages
      if (activeChatType === 'group') {
        await axios.post(`${BACKEND_URL}/api/staff-chat/groups/${activeChat}/send`, {
          chat_id: activeChat,
          message: inputMessage,
          image_url: imageUrl
        }, { headers });
        
        setInputMessage('');
        setSelectedImage(null);
        setImagePreview(null);
        setReplyingTo(null);
        setShowEmojiPicker(false);
        // Clear typing indicator after sending
        setIAmTyping(false);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        const clearHeaders = getAuthHeaders();
        if (clearHeaders) axios.delete(`${BACKEND_URL}/api/staff-chat/typing/${activeChat}`, { headers: clearHeaders }).catch(() => {});
        fetchGroupMessages(activeChat);
      } else {
        // For channels and DMs
        const isPrivate = activeChatType === 'dm';
        await axios.post(`${BACKEND_URL}/api/staff-chat/send`, {
          chat_id: activeChat,
          message: inputMessage,
          is_private: isPrivate,
          image_url: imageUrl
        }, { headers });

        setInputMessage('');
        setSelectedImage(null);
        setImagePreview(null);
        setReplyingTo(null);
        setShowEmojiPicker(false);
        // Clear typing indicator after sending
        setIAmTyping(false);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        const clearHeaders2 = getAuthHeaders();
        if (clearHeaders2) axios.delete(`${BACKEND_URL}/api/staff-chat/typing/${activeChat}`, { headers: clearHeaders2 }).catch(() => {});
        fetchMessages(activeChat);
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleAIMessage = async (overrideMessage) => {
    const msgText = overrideMessage || inputMessage;
    if (!msgText.trim()) return;
    const userMsg = { role: 'user', content: msgText, timestamp: new Date().toISOString() };
    const newMessages = [...aiMessages, userMsg];
    setAiMessages(newMessages);
    setInputMessage('');
    setAiLoading(true);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const historyForApi = newMessages.slice(-8).map(m => ({ sender: m.role, message: m.content }));
      const response = await axios.post(`${BACKEND_URL}/api/staff-chat/ai-assistant`, { 
        message: msgText,
        conversation_history: historyForApi
      }, { headers });
      const aiResponse = { role: 'assistant', content: response.data.response, timestamp: new Date().toISOString() };
      const updatedMessages = [...newMessages, aiResponse];
      setAiMessages(updatedMessages);
      localStorage.setItem(`staffchat_ai_history_${currentUser?.username}`, JSON.stringify(updatedMessages));
    } catch (error) {
      toast.error('Nova is temporarily unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  const handleClearAIChat = () => {
    setAiMessages([]);
    localStorage.removeItem(`staffchat_ai_history_${currentUser?.username}`);
    toast.success('Conversation cleared');
  };

  const handleCopyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || selectedGroupMembers.length < 1) {
      toast.error('Please provide a name and select at least one member');
      return;
    }
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.post(`${BACKEND_URL}/api/staff-chat/groups`, {
        name: newGroupName,
        members: [...selectedGroupMembers, currentUser?.username]
      }, { headers });

      // Upload group avatar if selected
      if (groupAvatarFile && res.data?.group_id) {
        const formData = new FormData();
        formData.append('image', groupAvatarFile);
        await axios.post(`${BACKEND_URL}/api/staff-chat/groups/${res.data.group_id}/avatar`, formData, {
          headers: { Authorization: headers.Authorization }
        });
      }

      toast.success('Group created!');
      setShowCreateGroupModal(false);
      setNewGroupName('');
      setSelectedGroupMembers([]);
      setGroupAvatarFile(null);
      setGroupAvatarPreview(null);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleRemoveMember = async (groupId, memberUsername) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BACKEND_URL}/api/staff-chat/groups/${groupId}`, {
        remove_members: [memberUsername]
      }, { headers });
      toast.success(`${memberUsername} removed from group`);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleStartGroupCall = (groupId) => {
    // Get group details and initiate call
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    // Open conference page with group members
    const membersList = group.members?.filter(m => m !== currentUser?.username).join(',');
    window.open(`/staff/conference?group=${groupId}&members=${membersList}`, '_blank');
    toast.success('Starting group call...');
  };

  const handlePinUser = (username) => {
    let newPinned;
    if (pinnedUsers.includes(username)) {
      newPinned = pinnedUsers.filter(u => u !== username);
    } else {
      newPinned = [username, ...pinnedUsers];
    }
    setPinnedUsers(newPinned);
    localStorage.setItem('staffchat_pinned', JSON.stringify(newPinned));
  };

  const handleMentionSelect = (staff) => {
    const lastAtIndex = inputMessage.lastIndexOf('@');
    const newMessage = inputMessage.slice(0, lastAtIndex) + `@${staff.username} `;
    setInputMessage(newMessage);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  // ==================== NEW FEATURE HANDLERS ====================

  const handleReaction = async (messageId, emoji) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.post(`${BACKEND_URL}/api/staff-chat/messages/${messageId}/react`, { emoji }, { headers });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch { toast.error('Failed to react'); }
  };

  const handleBookmarkToggle = async (messageId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.post(`${BACKEND_URL}/api/staff-chat/messages/${messageId}/bookmark`, {}, { headers });
      toast.success(res.data.status === 'added' ? 'Message saved' : 'Bookmark removed');
      fetchBookmarks();
    } catch { toast.error('Failed to bookmark'); }
  };

  const fetchBookmarks = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/staff-chat/bookmarks`, { headers });
      setBookmarks(res.data || []);
    } catch { setBookmarks([]); }
  };

  const handleForwardMessage = async (messageId, targetChatId, targetType) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/staff-chat/messages/${messageId}/forward`, {
        target_chat_id: targetChatId, target_type: targetType
      }, { headers });
      toast.success('Message forwarded');
    } catch { toast.error('Failed to forward'); }
  };

  const handlePinMessage = async (messageId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.post(`${BACKEND_URL}/api/staff-chat/messages/${messageId}/pin`, {}, { headers });
      toast.success(res.data.is_pinned ? 'Message pinned' : 'Message unpinned');
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_pinned: res.data.is_pinned } : m));
      if (activeChatType === 'channel') fetchPinnedMessages(activeChat);
    } catch { toast.error('Failed to pin'); }
  };

  const fetchPinnedMessages = async (channelId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/staff-chat/channels/${channelId}/pinned`, { headers });
      setPinnedMessages(res.data || []);
    } catch { setPinnedMessages([]); }
  };

  const fetchChannelInfo = async (channelId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/staff-chat/channels/${channelId}/info`, { headers });
      setChannelInfo(res.data);
    } catch { setChannelInfo(null); }
  };

  const handleSetChannelTopic = async (topic) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/staff-chat/channels/${activeChat}/topic`, { topic }, { headers });
      toast.success('Topic updated');
      fetchChannelInfo(activeChat);
    } catch { toast.error('Failed to set topic'); }
  };

  const handleVoiceSend = async (audioBlob) => {
    setIsRecording(false);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice.webm');
      const uploadRes = await axios.post(`${BACKEND_URL}/api/staff-chat/upload-voice`, formData, {
        headers: { Authorization: headers.Authorization }
      });
      const voiceUrl = uploadRes.data.voice_url;
      if (activeChatType === 'group') {
        await axios.post(`${BACKEND_URL}/api/staff-chat/groups/${activeChat}/send`, {
          chat_id: activeChat, message: '', voice_url: voiceUrl
        }, { headers });
        fetchGroupMessages(activeChat);
      } else {
        await axios.post(`${BACKEND_URL}/api/staff-chat/send`, {
          chat_id: activeChat, message: '', is_private: activeChatType === 'dm', voice_url: voiceUrl
        }, { headers });
        fetchMessages(activeChat);
      }
      toast.success('Voice message sent');
    } catch { toast.error('Failed to send voice message'); }
  };

  const handleScrollCheck = useCallback((e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setShowScrollBottom(!atBottom);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollBottom(false);
  };

  // Date separator helper
  const getDateLabel = (timestamp) => {
    if (!timestamp) return null;
    const d = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diff = today - msgDate;
    if (diff === 0) return 'Today';
    if (diff <= 86400000) return 'Yesterday';
    return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  };

  // Edit / Delete handlers
  const handleStartEdit = (msg) => {
    setEditingMessageId(msg.id);
    setEditText(msg.message);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  const handleSaveEdit = async (messageId) => {
    if (!editText.trim()) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BACKEND_URL}/api/staff-chat/messages/${messageId}`, { message: editText }, { headers });
      toast.success('Message edited');
      setEditingMessageId(null);
      setEditText('');
      if (activeChatType === 'group') fetchGroupMessages(activeChat);
      else fetchMessages(activeChat);
    } catch (error) {
      toast.error('Failed to edit message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.delete(`${BACKEND_URL}/api/staff-chat/messages/${messageId}`, { headers });
      toast.success('Message deleted');
      setShowDeleteConfirm(null);
      if (activeChatType === 'group') fetchGroupMessages(activeChat);
      else fetchMessages(activeChat);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const canModifyMessage = (msg) => {
    if (!msg.id) return false;
    if (msg.is_deleted) return false;
    return msg.sender_username === currentUser?.username;
  };

  const canDeleteMessage = (msg) => {
    if (!msg.id) return false;
    if (msg.is_deleted) return false;
    if (msg.sender_username === currentUser?.username) return true;
    if (isManager) return true;
    return false;
  };

  // Utilities
  const getStaffStatus = (username) => {
    const status = onlineStatus[username];
    if (!status) return { online: false, lastSeen: null };
    const lastActive = new Date(status.last_active);
    const now = new Date();
    const diffMinutes = Math.floor((now - lastActive) / 60000);
    return {
      online: diffMinutes < 5,
      lastSeen: diffMinutes < 60 ? `${diffMinutes}m ago` : diffMinutes < 1440 ? `${Math.floor(diffMinutes / 60)}h ago` : null
    };
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStaffInfo = (username) => {
    return staffList.find(s => s.username === username) || { username, display_name: username };
  };

  const [searchMode, setSearchMode] = useState('people'); // 'people' or 'messages'

  const filteredStaffList = staffList.filter(staff => {
    if (searchMode === 'messages' && searchQuery) return false; // Hide staff list when searching messages
    const matchesSearch = staff.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (staff.display_name && staff.display_name.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  const searchResults = searchMode === 'messages' && searchQuery.length >= 2
    ? messages.filter(m => m.content?.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 20)
    : [];

  const mentionableStaff = staffList.filter(s => 
    s.username.toLowerCase().includes(mentionFilter) ||
    (s.display_name && s.display_name.toLowerCase().includes(mentionFilter))
  ).slice(0, 5);

  const getChatName = () => {
    if (activeChatType === 'ai') return 'Nova';
    if (activeChatType === 'channel') {
      const channel = TEAM_CHANNELS.find(c => c.id === activeChat);
      return channel?.name || activeChat;
    }
    if (activeChatType === 'group') {
      const group = groups.find(g => g.id === activeChat);
      return group?.name || 'Group Chat';
    }
    const staff = getStaffInfo(activeChat);
    return staff.display_name || staff.username;
  };

  const getChatDescription = () => {
    if (activeChatType === 'ai') return 'Your AI workplace assistant';
    if (activeChatType === 'channel') {
      if (channelInfo?.topic) return channelInfo.topic;
      const channel = TEAM_CHANNELS.find(c => c.id === activeChat);
      return `${channelInfo?.online_members || 0} online  \u00B7  ${channelInfo?.message_count || 0} messages`;
    }
    if (activeChatType === 'group') {
      const group = groups.find(g => g.id === activeChat);
      return `${group?.members?.length || 0} members`;
    }
    const status = getStaffStatus(activeChat);
    return status.online ? 'Online' : status.lastSeen || 'Offline';
  };

  const onlineCount = Object.values(onlineStatus).filter(s => {
    const lastActive = new Date(s.last_active);
    const now = new Date();
    return Math.floor((now - lastActive) / 60000) < 5;
  }).length;

  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  return (
    <div className="h-[calc(100vh-100px)] flex rounded-2xl overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#05050A] via-[#0a0d1a] to-[#05050A] shadow-2xl shadow-black/40 relative" data-testid="staff-chat-page">
      {/* Ambient aurora — jewel tokens */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, #00E5FF33 0%, transparent 70%)' }} />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, #8A2BE233 0%, transparent 70%)' }} />
      </div>

      {/* Sidebar */}
      <div className="w-80 flex flex-col bg-white/[0.02] border-r border-white/[0.06] backdrop-blur-xl relative z-10">
        {/* Header */}
        <div className="p-4 border-b border-white/[0.05]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative w-11 h-11 rounded-2xl flex items-center justify-center ring-1 ring-white/10 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.28), rgba(138,43,226,0.28))', boxShadow: '0 0 24px rgba(0,229,255,0.18)' }}>
                <MessageSquare className="w-5 h-5 text-white" strokeWidth={2.2} />
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#05050A]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">Team channel</div>
                <h2 className="text-white font-light tracking-tight text-lg -mt-0.5">
                  Staff <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">Chat</span>
                </h2>
                <div className="flex items-center gap-1.5 -mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <p className="text-white/50 text-xs font-medium">{onlineCount} online</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button onClick={() => setShowCreateGroupModal(true)} size="sm" variant="ghost"
                className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/[0.05] rounded-lg">
                <Plus className="w-4 h-4" />
              </Button>
              <Button onClick={() => { setShowBookmarks(!showBookmarks); if (!showBookmarks) fetchBookmarks(); }} size="sm" variant="ghost"
                className={`h-8 w-8 p-0 rounded-lg ${showBookmarks ? 'text-amber-400 bg-amber-500/10' : 'text-white/50 hover:text-white hover:bg-white/[0.05]'}`}
                data-testid="bookmarks-toggle-btn">
                <Bookmark className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input type="text" placeholder={searchMode === 'messages' ? "Search message content..." : "Search people..."} value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl pl-10 pr-20 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-400/40 focus:bg-white/[0.05] transition-all" data-testid="chat-search-input" />
            <button onClick={() => { setSearchMode(searchMode === 'people' ? 'messages' : 'people'); setSearchQuery(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-lg bg-white/[0.05] text-[10px] font-semibold tracking-wider text-white/70 hover:text-white hover:bg-white/[0.1] transition-all"
              data-testid="search-mode-toggle">
              {searchMode === 'people' ? 'MSGS' : 'PEOPLE'}
            </button>
          </div>

          {/* Message Search Results */}
          {searchMode === 'messages' && searchQuery.length >= 2 && (
            <div className="max-h-60 overflow-y-auto space-y-0.5 px-1 py-1 border-b border-white/[0.06]" data-testid="message-search-results">
              {searchResults.length === 0 ? (
                <p className="text-xs text-white/40 text-center py-4">No messages found</p>
              ) : (
                searchResults.map((msg, i) => (
                  <button key={i} onClick={() => {
                    setActiveChat(msg.room || msg.sender);
                    setActiveChatType(msg.room ? 'channel' : 'dm');
                    setSearchQuery('');
                  }} className="w-full p-2 rounded-lg hover:bg-white/[0.02] text-left transition-all">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-medium text-indigo-400">{msg.sender_name || msg.sender}</span>
                      <span className="text-[10px] text-white/30">{formatTime(msg.timestamp)}</span>
                    </div>
                    <p className="text-xs text-white/50 line-clamp-2">{msg.content}</p>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Navigation Tabs — Jewel */}
        <div className="flex p-1.5 gap-1 border-b border-white/[0.05] bg-white/[0.01]">
          {[
            { id: 'channels', label: 'Channels', icon: Hash, accent: 'cyan' },
            { id: 'direct', label: 'Direct', icon: MessageCircle, accent: 'violet' },
            { id: 'groups', label: 'Groups', icon: Users, accent: 'emerald' },
          ].map(tab => {
            const active = sidebarView === tab.id;
            const accentColor = tab.accent === 'cyan' ? 'cyan' : tab.accent === 'violet' ? 'violet' : 'emerald';
            return (
              <button key={tab.id} onClick={() => setSidebarView(tab.id)}
                data-testid={`sidebar-tab-${tab.id}`}
                className={`relative flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold tracking-wide transition-all overflow-hidden group ${
                  active
                    ? `bg-gradient-to-br from-${accentColor}-500/20 to-purple-500/10 text-white border border-${accentColor}-400/30 shadow-[0_0_20px_-5px_rgba(0,229,255,0.3)]`
                    : 'text-white/50 hover:text-white/90 hover:bg-white/[0.03] border border-transparent'
                }`}>
                {active && <span className="absolute inset-x-2 -bottom-px h-[2px] bg-gradient-to-r from-transparent via-white/60 to-transparent" />}
                <tab.icon className={`w-3.5 h-3.5 transition-transform ${active ? 'scale-110' : 'group-hover:scale-105'}`} strokeWidth={active ? 2 : 1.5} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Sidebar Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Nova AI Assistant - Always visible */}
          <div className="p-2 border-b border-white/[0.06]">
            <button onClick={() => { setActiveChat('ai'); setActiveChatType('ai'); }}
              data-testid="nova-ai-sidebar-btn"
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                activeChatType === 'ai'
                  ? 'bg-gradient-to-r from-cyan-500/15 to-purple-500/15 border border-cyan-500/30 shadow-[0_0_20px_rgba(0,229,255,0.08)]'
                  : 'hover:bg-white/[0.02]'
              }`}>
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <Brain className="w-5 h-5 text-white" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#05050A]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-semibold text-sm tracking-wide">Nova</p>
                <p className="text-white/40 text-xs">AI Workplace Assistant</p>
              </div>
              <Sparkles className="w-4 h-4 text-cyan-400 animate-pulse" />
            </button>
          </div>

          {/* Channels View */}
          {sidebarView === 'channels' && (
            <div className="p-2 space-y-1">
              <p className="text-white/40 text-xs font-medium px-2 py-1">CHANNELS</p>
              {TEAM_CHANNELS.map(channel => {
                if (channel.restricted && !isManager) return null;
                const ChannelIcon = channel.icon;
                const isActive = activeChatType === 'channel' && activeChat === channel.id;
                const unread = unreadCounts[channel.id] || 0;
                
                return (
                  <button key={channel.id}
                    onClick={() => { setActiveChat(channel.id); setActiveChatType('channel'); }}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                      isActive
                        ? `bg-${channel.color}-500/20 border border-${channel.color}-500/30`
                        : 'hover:bg-white/[0.02]'
                    }`}>
                    <div className={`w-8 h-8 rounded-lg bg-${channel.color}-500/20 flex items-center justify-center`}>
                      <ChannelIcon className={`w-4 h-4 text-${channel.color}-400`} />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium text-sm truncate ${isActive ? 'text-white' : 'text-white/70'}`}>
                          {channel.name}
                        </p>
                        {channel.restricted && <Lock className="w-3 h-3 text-white/40" />}
                      </div>
                      <p className="text-white/40 text-xs truncate">{channel.desc}</p>
                    </div>
                    {unread > 0 && (
                      <Badge className={`bg-${channel.color}-500 text-white text-[10px] min-w-[20px] h-5`}>{unread}</Badge>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Direct Messages View */}
          {sidebarView === 'direct' && (
            <div className="p-2 space-y-1">
              {/* Pinned Users */}
              {pinnedUsers.length > 0 && (
                <>
                  <p className="text-white/40 text-xs font-medium px-2 py-1 flex items-center gap-1">
                    <Pin className="w-3 h-3" /> PINNED
                  </p>
                  {pinnedUsers.map(username => {
                    const staff = getStaffInfo(username);
                    const status = getStaffStatus(username);
                    const isActive = activeChatType === 'dm' && activeChat === username;
                    
                    return (
                      <button key={username}
                        onClick={() => { setActiveChat(username); setActiveChatType('dm'); }}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all ${
          isActive ? 'bg-gradient-to-br from-violet-500/25 to-indigo-500/15 border border-violet-400/50 shadow-[0_0_20px_-4px_rgba(138,43,226,0.4),inset_0_1px_0_rgba(255,255,255,0.05)] animate-[jewelFadeUp_0.3s_ease-out]' : 'hover:bg-white/[0.02] border border-transparent'
                        }`}
                        data-active={isActive}
                        style={isActive ? { animation: 'jewelPulseGlow 3s ease-in-out infinite' } : {}}
                        >
                        <div className="relative">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/30 to-purple-500/30 flex items-center justify-center overflow-hidden">
                            {staff.avatar ? (
                              <img src={getImageUrl(staff.avatar)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-white font-bold text-sm">{staff.display_name?.charAt(0) || 'S'}</span>
                            )}
                          </div>
                          {status.online && (
                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#05050A]" />
                          )}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className={`font-medium text-sm truncate ${isActive ? 'text-white' : 'text-white/70'}`}>
                            {staff.display_name || username}
                          </p>
                          <p className={`text-xs ${status.online ? 'text-emerald-400' : 'text-white/40'}`}>
                            {status.online ? 'Online' : status.lastSeen || 'Offline'}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
              
              {/* All Staff */}
              <p className="text-white/40 text-xs font-medium px-2 py-1 mt-2">ALL TEAM</p>
              {filteredStaffList.map(staff => {
                const status = getStaffStatus(staff.username);
                const isActive = activeChatType === 'dm' && activeChat === staff.username;
                const unread = unreadCounts[staff.username] || 0;
                
                return (
                  <button key={staff.username}
                    onClick={() => { setActiveChat(staff.username); setActiveChatType('dm'); }}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                      isActive ? 'bg-gradient-to-br from-violet-500/25 to-indigo-500/15 border border-violet-400/50 shadow-[0_0_20px_-4px_rgba(138,43,226,0.4),inset_0_1px_0_rgba(255,255,255,0.05)] animate-[jewelFadeUp_0.3s_ease-out]' : 'hover:bg-white/[0.02] border border-transparent'
                    }`}
                    data-active={isActive}
                    style={isActive ? { animation: 'jewelPulseGlow 3s ease-in-out infinite' } : {}}
                    >
                    <div className="relative">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-700 to-slate-600 flex items-center justify-center overflow-hidden">
                        {staff.avatar ? (
                          <img src={getImageUrl(staff.avatar)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-semibold text-sm">{staff.display_name?.charAt(0) || 'S'}</span>
                        )}
                      </div>
                      {status.online && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#05050A]" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className={`font-medium text-sm truncate ${isActive ? 'text-white' : 'text-white/70'}`}>
                        {staff.display_name || staff.username}
                      </p>
                      <p className="text-white/40 text-xs truncate">{staff.role || 'Staff'}</p>
                    </div>
                    {unread > 0 && (
                      <Badge className="bg-indigo-500 text-white text-[10px] min-w-[20px] h-5">{unread}</Badge>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Groups View */}
          {sidebarView === 'groups' && (
            <div className="p-2 space-y-1">
              <p className="text-white/40 text-xs font-medium px-2 py-1">MY GROUPS</p>
              {groups.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                  <p className="text-white/40 text-sm">No groups yet</p>
                  <Button onClick={() => setShowCreateGroupModal(true)} size="sm" className="mt-3 bg-indigo-500 hover:bg-indigo-600">
                    <Plus className="w-4 h-4 mr-1" /> Create Group
                  </Button>
                </div>
              ) : (
                groups.map(group => {
                  const isActive = activeChatType === 'group' && activeChat === group.id;
                  const unread = unreadCounts[group.id] || 0;
                  
                  return (
                    <button key={group.id}
                      onClick={() => { setActiveChat(group.id); setActiveChatType('group'); }}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                        isActive ? 'bg-indigo-500/20 border border-indigo-500/30' : 'hover:bg-white/[0.02]'
                      }`}>
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500/30 to-cyan-500/30 flex items-center justify-center overflow-hidden">
                        {group.avatar ? (
                          <img src={getImageUrl(group.avatar)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-4 h-4 text-teal-400" />
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className={`font-medium text-sm truncate ${isActive ? 'text-white' : 'text-white/70'}`}>
                          {group.name}
                        </p>
                        <p className="text-white/40 text-xs">{group.members?.length || 0} members</p>
                      </div>
                      {unread > 0 && (
                        <Badge className="bg-teal-500 text-white text-[10px] min-w-[20px] h-5">{unread}</Badge>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat + Members Panel wrapper */}
      <div className="flex-1 flex overflow-hidden relative z-10">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="relative h-16 px-5 border-b border-white/[0.05] flex items-center justify-between bg-[#0a0d1a]/30 backdrop-blur-xl overflow-hidden">
          {/* Subtle aurora line accent */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400/30 to-transparent" />
          <div className="absolute -top-12 left-1/4 w-64 h-32 bg-indigo-500/[0.04] blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-3">
            {activeChatType === 'ai' ? (
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <Brain className="w-5 h-5 text-white" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#05050A]" />
              </div>
            ) : activeChatType === 'channel' ? (
              <div className={`w-10 h-10 rounded-xl bg-${TEAM_CHANNELS.find(c => c.id === activeChat)?.color || 'slate'}-500/20 flex items-center justify-center`}>
                {(() => {
                  const channel = TEAM_CHANNELS.find(c => c.id === activeChat);
                  const ChannelIcon = channel?.icon || Hash;
                  return <ChannelIcon className={`w-5 h-5 text-${channel?.color || 'slate'}-400`} />;
                })()}
              </div>
            ) : activeChatType === 'dm' ? (
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/30 to-purple-500/30 flex items-center justify-center overflow-hidden">
                  {(() => {
                    const staff = getStaffInfo(activeChat);
                    return staff.avatar ? (
                      <img src={getImageUrl(staff.avatar)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white font-bold">{staff.display_name?.charAt(0) || 'S'}</span>
                    );
                  })()}
                </div>
                {getStaffStatus(activeChat).online && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#05050A]" />
                )}
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center overflow-hidden">
                {(() => {
                  const group = groups.find(g => g.id === activeChat);
                  return group?.avatar ? (
                    <img src={getImageUrl(group.avatar)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-5 h-5 text-teal-400" />
                  );
                })()}
              </div>
            )}
            
            <div>
              <h3 className="text-white font-semibold">{getChatName()}</h3>
              <p className={`text-xs ${
                activeChatType === 'dm' && getStaffStatus(activeChat).online 
                  ? 'text-emerald-400' 
                  : 'text-white/40'
              }`}>{getChatDescription()}</p>
            </div>
          </div>
          
          {/* Header Actions */}
          <div className="relative flex items-center gap-2">
            {activeChatType === 'ai' && aiMessages.length > 0 && (
              <Button onClick={handleClearAIChat} size="sm" variant="ghost" data-testid="nova-clear-chat-btn"
                className="h-8 px-3 text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg text-xs gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Clear
              </Button>
            )}
            {activeChatType === 'channel' && (
              <>
                {pinnedMessages.length > 0 && (
                  <Button onClick={() => setShowChannelPanel(true)} size="sm" variant="ghost"
                    className="h-8 px-2.5 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg text-xs gap-1.5" data-testid="pinned-msgs-btn">
                    <Pin className="w-3.5 h-3.5" /> {pinnedMessages.length}
                  </Button>
                )}
                <Button onClick={() => { setShowChannelPanel(!showChannelPanel); if (!showChannelPanel) fetchChannelInfo(activeChat); }} size="sm" variant="ghost"
                  className={`h-8 w-8 p-0 rounded-lg ${showChannelPanel ? 'text-cyan-400 bg-cyan-500/10' : 'text-white/50 hover:text-white hover:bg-white/[0.02]'}`}
                  data-testid="channel-info-btn">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
                <Button onClick={() => setShowMembers(!showMembers)} size="sm" variant="ghost"
                  className={`h-8 w-8 p-0 rounded-lg ${showMembers ? 'text-white bg-white/[0.02]' : 'text-white/50 hover:text-white hover:bg-white/[0.02]'}`}
                  data-testid="members-toggle-btn">
                  <Users className="w-4 h-4" />
                </Button>
              </>
            )}
            {activeChatType === 'dm' && (
              <>
                <CallButtons username={activeChat} displayName={getChatName()} />
                <Button onClick={() => handlePinUser(activeChat)} size="sm" variant="ghost"
                  className={`h-8 w-8 p-0 ${pinnedUsers.includes(activeChat) ? 'text-amber-400' : 'text-white/50 hover:text-white'}`}>
                  {pinnedUsers.includes(activeChat) ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                </Button>
              </>
            )}
            {activeChatType === 'group' && (
              <>
                <Button onClick={() => handleStartGroupCall(activeChat)} size="sm"
                  className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 h-8">
                  <Video className="w-4 h-4 mr-1" /> Call
                </Button>
                <Button onClick={() => setShowMembers(!showMembers)} size="sm" variant="ghost"
                  className={`h-8 w-8 p-0 rounded-lg ${showMembers ? 'text-white bg-white/[0.02]' : 'text-white/50 hover:text-white hover:bg-white/[0.02]'}`}>
                  <Users className="w-4 h-4" />
                </Button>
                <Button onClick={() => setShowGroupSettingsModal(true)} size="sm" variant="ghost"
                  className="h-8 w-8 p-0 text-white/50 hover:text-white">
                  <Settings className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-2 relative" ref={messagesContainerRef} onScroll={handleScrollCheck}>
          {/* Channel Topic Banner */}
          {activeChatType === 'channel' && channelInfo?.topic && (
            <div className="bg-white/[0.02] border-b border-white/[0.06] px-4 py-2" data-testid="channel-topic-banner">
              <p className="text-white/40 text-xs truncate">{channelInfo.topic}</p>
            </div>
          )}
          {activeChatType === 'ai' ? (
            // Nova AI Messages
            <>
              {aiMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center px-6" data-testid="nova-welcome-screen">
                  {/* Nova Avatar */}
                  <div className="relative mb-6">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-cyan-500/20">
                      <Brain className="w-12 h-12 text-white" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center border-2 border-slate-950">
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-white font-bold text-xl mb-1 tracking-tight">Hey, I'm Nova</h3>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500/15 to-purple-500/15 border border-cyan-400/20 mb-3">
                    <Sparkles className="w-3 h-3 text-cyan-300" />
                    <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">Premium · Starnight Edition</span>
                  </div>
                  <p className="text-white/50 text-sm mb-8 max-w-md leading-relaxed">
                    Your elite AI workplace partner. I know our leadership, policies, and portfolio inside out — ready to draft emails, handle tricky situations, and help you move faster.
                  </p>
                  
                  {/* Suggestion Chips */}
                  <div className="grid grid-cols-2 gap-2.5 max-w-lg w-full" data-testid="nova-suggestion-chips">
                    {NOVA_SUGGESTIONS.map((suggestion, idx) => {
                      const SugIcon = suggestion.icon;
                      return (
                        <button key={idx}
                          onClick={() => handleAIMessage(suggestion.prompt)}
                          data-testid={`nova-suggestion-${idx}`}
                          className={`group flex items-center gap-3 p-3.5 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-${suggestion.color}-500/10 hover:border-${suggestion.color}-500/20 transition-all duration-200 text-left`}>
                          <div className={`w-9 h-9 rounded-lg bg-${suggestion.color}-500/15 flex items-center justify-center flex-shrink-0`}>
                            <SugIcon className={`w-4 h-4 text-${suggestion.color}-400`} />
                          </div>
                          <span className="text-white/70 text-xs font-medium group-hover:text-white transition-colors">{suggestion.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  
                  <p className="text-white/30 text-[11px] mt-6">Powered by GPT-5.2 · Encrypted · Audited · Always verify critical info</p>
                </div>
              )}
              
              {aiMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-[fadeInUp_0.3s_ease-out]`}
                  data-testid={`nova-message-${idx}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center flex-shrink-0 mr-2.5 mt-1 shadow-lg shadow-cyan-500/10">
                      <Brain className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className={`max-w-[70%] relative group ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-indigo-500/15 to-purple-500/15 border border-indigo-500/20 rounded-2xl rounded-br-md p-4'
                      : 'bg-white/[0.03] border border-white/[0.06] rounded-2xl rounded-bl-md p-4'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-cyan-400 text-xs font-semibold tracking-wide">Nova</span>
                        </div>
                        <button onClick={() => handleCopyMessage(msg.content)}
                          data-testid={`nova-copy-${idx}`}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-white/40 hover:text-white p-1 rounded-md hover:bg-white/10">
                          <Clipboard className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {msg.role === 'assistant' ? (
                      <div className="nova-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-white text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                    <p className="text-white/30 text-[10px] mt-2">{formatTime(msg.timestamp)}</p>
                  </div>
                </div>
              ))}
              
              {aiLoading && (
                <div className="flex justify-start animate-[fadeInUp_0.3s_ease-out]" data-testid="nova-loading">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center flex-shrink-0 mr-2.5 mt-1">
                    <Brain className="w-4 h-4 text-white animate-pulse" />
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl rounded-bl-md px-5 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-cyan-400 text-xs font-semibold mr-2">Nova is thinking</span>
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" />
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Regular Messages — Discord flat style
            <>
            {messages.length === 0 && activeChatType === 'channel' && (
              <div className="flex flex-col items-center justify-center h-full text-center px-6" data-testid="channel-welcome">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 border border-cyan-500/20 flex items-center justify-center mb-4">
                  <Hash className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-1">Welcome to #{getChatName()}</h3>
                <p className="text-white/50 text-sm mb-4 max-w-sm">This is the start of the channel. Send a message to get the conversation going.</p>
              </div>
            )}
            {messages.map((msg, idx) => {
              const displayName = msg.sender_display_name || msg.sender_username || 'Unknown';
              const avatarUrl = msg.sender_avatar ? getImageUrl(msg.sender_avatar) : null;
              const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
              const isEditing = editingMessageId && msg.id && editingMessageId === msg.id;
              const canEdit = canModifyMessage(msg);
              const canDelete = canDeleteMessage(msg);
              const isHovered = hoveredMsgId === msg.id;
              const roleBadge = ROLE_BADGES[msg.sender_role] || ROLE_BADGES.staff;

              // Date separator
              const prevMsg = idx > 0 ? messages[idx - 1] : null;
              const currDate = getDateLabel(msg.timestamp);
              const prevDate = prevMsg ? getDateLabel(prevMsg.timestamp) : null;
              const showDateSep = currDate && currDate !== prevDate;

              // Message grouping — compact if same sender within 5 min
              const isGrouped = prevMsg && !showDateSep &&
                prevMsg.sender_username === msg.sender_username &&
                !msg.is_deleted && !prevMsg.is_deleted &&
                msg.timestamp && prevMsg.timestamp &&
                (new Date(msg.timestamp) - new Date(prevMsg.timestamp)) < 300000;

              return (
                <React.Fragment key={msg.id || idx}>
                  {showDateSep && (
                    <div className="flex items-center gap-3 py-2 mx-4" data-testid={`date-sep-${idx}`}>
                      <div className="flex-1 h-px bg-white/[0.02]" />
                      <span className="text-white/40 text-[10px] font-semibold uppercase tracking-wider px-2">{currDate}</span>
                      <div className="flex-1 h-px bg-white/[0.02]" />
                    </div>
                  )}

                  {/* Discord-flat message row */}
                  <div className={`relative flex items-start gap-4 px-4 py-0.5 hover:bg-white/[0.02] transition-colors ${isGrouped ? 'mt-0' : 'mt-3'}`}
                    onMouseEnter={() => setHoveredMsgId(msg.id)}
                    onMouseLeave={() => setHoveredMsgId(null)}
                    data-testid={`msg-row-${msg.id}`}>

                    {/* Avatar column — 40px wide */}
                    <div className="w-10 flex-shrink-0">
                      {!isGrouped ? (
                        <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity bg-white/[0.02]"
                          onClick={() => setProfileCardTarget(msg.sender_username)}>
                          {avatarUrl && !failedAvatars[avatarUrl] ? (
                            <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover"
                              onError={() => setFailedAvatars(prev => ({ ...prev, [avatarUrl]: true }))} />
                          ) : (
                            <span className="text-white text-sm font-semibold">{initials}</span>
                          )}
                        </div>
                      ) : (
                        <span className="hidden group-hover:block text-[10px] text-white/30 text-right w-10 pt-1 font-mono select-none">
                          {formatTime(msg.timestamp)}
                        </span>
                      )}
                    </div>

                    {/* Message content */}
                    <div className="flex-1 min-w-0">
                      {/* Name + Role Badge + Time (only for first message in group) */}
                      {!isGrouped && (
                        <div className="flex items-center gap-2 mb-0.5">
                          <button onClick={() => setProfileCardTarget(msg.sender_username)}
                            className="text-white font-medium text-sm hover:underline cursor-pointer" data-testid={`chat-user-${msg.sender_username}`}>
                            {displayName}
                          </button>
                          {roleBadge && (
                            <span className={`text-[9px] px-1.5 py-px rounded font-bold uppercase tracking-wider ${roleBadge.cls}`}>
                              {roleBadge.label}
                            </span>
                          )}
                          {msg.is_pinned && <Pin className="w-3 h-3 text-amber-400" />}
                          <span className="text-white/30 text-[11px]">{formatTime(msg.timestamp)}</span>
                        </div>
                      )}

                      {/* Message body */}
                      {msg.is_deleted ? (
                        <p className="text-white/40 text-sm italic">This message was deleted</p>
                      ) : isEditing ? (
                        <div className="space-y-2 max-w-lg">
                          <textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                            className="w-full bg-white/[0.02] border border-indigo-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-400 resize-none"
                            rows={2} autoFocus />
                          <div className="flex gap-1.5">
                            <span className="text-white/40 text-[10px]">escape to</span>
                            <button onClick={handleCancelEdit} className="text-cyan-400 text-[10px] hover:underline">cancel</button>
                            <span className="text-white/40 text-[10px]">  enter to</span>
                            <button onClick={() => handleSaveEdit(msg.id)} className="text-cyan-400 text-[10px] hover:underline">save</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {msg.forwarded_from && (
                            <div className="flex items-center gap-1.5 mb-1 text-white/40">
                              <Forward className="w-3 h-3" />
                              <span className="text-[10px] italic">Forwarded from {msg.forwarded_from.original_sender}</span>
                            </div>
                          )}
                          {msg.reply_to && (
                            <div className="flex items-center gap-1.5 mb-1 pl-3 border-l-2 border-slate-600">
                              <span className="text-white/40 text-xs">Replying to a message</span>
                            </div>
                          )}
                          {msg.voice_url ? (
                            <VoiceMessagePlayer voiceUrl={msg.voice_url} />
                          ) : msg.message && (
                            <p className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                          )}
                          {msg.image_url && (
                            <img src={getImageUrl(msg.image_url)} alt="Shared"
                              className="rounded-lg max-w-[320px] max-h-[220px] object-cover mt-1 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(getImageUrl(msg.image_url), '_blank')} />
                          )}
                          {msg.is_edited && <span className="text-white/30 text-[10px] ml-1">(edited)</span>}
                        </>
                      )}

                      {/* Reactions */}
                      {!msg.is_deleted && (
                        <ReactionDisplay reactions={msg.reactions} currentUser={currentUser?.username}
                          onReact={(emoji) => handleReaction(msg.id, emoji)} />
                      )}
                    </div>

                    {/* Hover action toolbar — top right of message */}
                    {isHovered && !msg.is_deleted && !isEditing && (
                      <div className="absolute right-4 -top-3 flex items-center gap-0.5 bg-white/[0.02] border border-white/[0.08] rounded-lg px-1 py-0.5 shadow-xl z-20 animate-[fadeIn_0.1s_ease-out]"
                        data-testid={`msg-actions-${msg.id}`}>
                        {REACTION_EMOJIS.slice(0, 4).map(em => (
                          <button key={em} onClick={() => handleReaction(msg.id, em)}
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/[0.02] text-sm hover:scale-110 transition-all">{em}</button>
                        ))}
                        <div className="w-px h-5 bg-white/[0.02] mx-0.5" />
                        <button onClick={() => handleBookmarkToggle(msg.id)}
                          className="w-7 h-7 flex items-center justify-center rounded hover:bg-amber-500/20 text-white/50 hover:text-amber-400 transition-all" title="Save">
                          <Bookmark className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setForwardingMessage(msg)}
                          className="w-7 h-7 flex items-center justify-center rounded hover:bg-cyan-500/20 text-white/50 hover:text-cyan-400 transition-all" title="Forward">
                          <Forward className="w-3.5 h-3.5" />
                        </button>
                        {isManager && activeChatType === 'channel' && (
                          <button onClick={() => handlePinMessage(msg.id)}
                            className={`w-7 h-7 flex items-center justify-center rounded hover:bg-amber-500/20 transition-all ${msg.is_pinned ? 'text-amber-400' : 'text-white/50 hover:text-amber-400'}`} title={msg.is_pinned ? 'Unpin' : 'Pin'}>
                            <Pin className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canEdit && (
                          <button onClick={() => handleStartEdit(msg)}
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-indigo-500/20 text-white/50 hover:text-indigo-400 transition-all" title="Edit">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => setShowDeleteConfirm(msg.id)}
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-500/20 text-white/50 hover:text-red-400 transition-all" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Delete confirmation */}
                    {showDeleteConfirm && msg.id && showDeleteConfirm === msg.id && (
                      <div className="absolute right-4 -top-3 z-30 bg-white/[0.02] border border-red-500/30 rounded-lg p-2 flex items-center gap-2 shadow-xl">
                        <span className="text-red-400 text-xs">Delete?</span>
                        <Button size="sm" onClick={() => handleDeleteMessage(msg.id)}
                          className="h-6 px-2 text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/40">Yes</Button>
                        <Button size="sm" onClick={() => setShowDeleteConfirm(null)}
                          className="h-6 px-2 text-[10px] bg-white/[0.02] text-white/70 hover:bg-slate-600">No</Button>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
            {/* Typing Indicator — Premium glow style */}
            {typingUsers.length > 0 && (
              <div className="flex items-center gap-4 px-4 py-1.5">
                <div className="w-10 flex-shrink-0" />
                <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-500/8 to-purple-500/8 border border-indigo-400/15 backdrop-blur-sm">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full animate-bounce shadow-sm shadow-indigo-400/60" />
                    <div className="w-1.5 h-1.5 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full animate-bounce shadow-sm shadow-indigo-400/60" style={{ animationDelay: '0.15s' }} />
                    <div className="w-1.5 h-1.5 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full animate-bounce shadow-sm shadow-indigo-400/60" style={{ animationDelay: '0.3s' }} />
                  </div>
                  <span className="text-white/70 text-[11px] font-medium">
                    <span className="text-white">{typingUsers.map(t => t.display_name).join(', ')}</span> {typingUsers.length === 1 ? 'is' : 'are'} typing
                  </span>
                </div>
              </div>
            )}
            </>
          )}
          <div ref={messagesEndRef} />

          {/* Scroll to bottom button */}
          {showScrollBottom && (
            <button onClick={scrollToBottom}
              className="sticky bottom-2 left-1/2 -translate-x-1/2 bg-white/[0.02] border border-white/[0.08] rounded-full px-4 py-2 text-xs text-white/70 hover:text-white hover:bg-white/[0.02] shadow-xl transition-all flex items-center gap-1.5 animate-[fadeIn_0.2s_ease-out] z-10"
              data-testid="scroll-to-bottom-btn">
              <ChevronDown className="w-3.5 h-3.5" />
              New messages below
            </button>
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-white/[0.06] bg-[#0a0d1a]/30">
          {/* Reply Preview */}
          {replyingTo && (
            <div className="mb-3 p-3 bg-white/[0.02] rounded-xl border border-white/[0.08] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Reply className="w-4 h-4 text-indigo-400" />
                <p className="text-white/50 text-sm">Replying to <span className="text-white">{replyingTo.sender}</span></p>
              </div>
              <button onClick={() => setReplyingTo(null)} className="text-white/40 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className="mb-3 p-3 bg-white/[0.02] border border-white/[0.08] rounded-xl">
              <div className="flex flex-wrap gap-2">
                {EMOJI_LIST.map((emoji, i) => (
                  <button key={i} onClick={() => setInputMessage(prev => prev + emoji)}
                    className="text-xl hover:scale-125 transition-transform p-1 hover:bg-white/[0.02] rounded">
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mentions Dropdown */}
          {showMentions && mentionableStaff.length > 0 && (
            <div className="mb-3 bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden">
              {mentionableStaff.map(staff => (
                <button key={staff.username} onClick={() => handleMentionSelect(staff)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/[0.02] transition-colors text-left">
                  <div className="w-8 h-8 rounded-lg bg-slate-600 flex items-center justify-center">
                    <span className="text-white text-sm">{staff.display_name?.charAt(0) || 'S'}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm">{staff.display_name || staff.username}</p>
                    <p className="text-white/40 text-xs">@{staff.username}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Image Preview */}
          {imagePreview && (
            <div className="mb-3 relative inline-block">
              <img src={imagePreview} alt="Preview" className="h-20 rounded-xl" />
              <button onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Input Row */}
          {isRecording ? (
            <VoiceRecorderBar onSend={handleVoiceSend} onCancel={() => setIsRecording(false)} />
          ) : (
            <div className="flex items-center gap-3">
              <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
              
              <div className="flex items-center gap-1 bg-white/[0.04] border border-white/[0.05] rounded-xl p-1">
                <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}
                  className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/[0.08] rounded-lg">
                  <ImageIcon className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className={`h-8 w-8 p-0 rounded-lg ${showEmojiPicker ? 'text-amber-400 bg-amber-500/20' : 'text-white/50 hover:text-white hover:bg-white/[0.08]'}`}>
                  <Smile className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost"
                  onClick={() => setInputMessage(prev => prev + '@')}
                  className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/[0.08] rounded-lg">
                  <AtSign className="w-4 h-4" />
                </Button>
                {activeChatType !== 'ai' && (
                  <VoiceRecorderButton onStartRecording={() => setIsRecording(true)} isRecording={isRecording} />
                )}
              </div>

              <input ref={inputRef} type="text" value={inputMessage}
                onChange={handleTypingInput}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder={activeChatType === 'ai' ? 'Ask Nova anything...' : activeChatType === 'channel' ? `Message #${getChatName()}` : activeChatType === 'group' ? `Message ${getChatName()}` : `Message @${getChatName()}`}
                className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-400/40 focus:ring-2 focus:ring-indigo-500/15 focus:bg-white/[0.05] transition-all" />

              <Button onClick={handleSendMessage} disabled={!inputMessage.trim() && !selectedImage}
                className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:opacity-90 disabled:opacity-40 text-white h-11 px-5 rounded-xl shadow-lg shadow-purple-500/25 transition-all hover:translate-y-[-1px]">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Members Panel — Discord style right sidebar */}
      {showMembers && (activeChatType === 'channel' || activeChatType === 'group') && (
        <ChatMembersPanel
          staffList={staffList}
          onlineStatus={onlineStatus}
          currentUser={currentUser}
          onUserClick={(username) => setProfileCardTarget(username)}
          groupMembers={activeChatType === 'group' ? groups.find(g => g.id === activeChat)?.members : null}
        />
      )}
      </div>{/* end of Main Chat + Members wrapper */}

      {/* Create Group Modal — Jewel */}
      <JewelModal
        open={showCreateGroupModal}
        onOpenChange={setShowCreateGroupModal}
        icon={Users}
        title="Create Group"
        subtitle={selectedGroupMembers.length > 0 ? `${selectedGroupMembers.length} teammate${selectedGroupMembers.length === 1 ? '' : 's'} selected` : "A private space for a specific mission"}
        accent="cyan"
        size="md"
        testid="create-group-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowCreateGroupModal(false)}>Cancel</JewelButton>
          <JewelButton accent="cyan" onClick={handleCreateGroup} icon={Plus} data-testid="group-create-btn">Create group</JewelButton>
        </>}
      >
        <div className="space-y-4">
          {/* Group avatar + name */}
          <div className="flex items-center gap-4">
            <input type="file" ref={groupAvatarInputRef} onChange={(e) => {
              const file = e.target.files[0];
              if (file) { setGroupAvatarFile(file); setGroupAvatarPreview(URL.createObjectURL(file)); }
            }} accept="image/*" className="hidden" />
            <button onClick={() => groupAvatarInputRef.current?.click()}
              className="w-16 h-16 rounded-2xl bg-white/[0.03] border-2 border-dashed border-white/10 hover:border-cyan-400/40 flex items-center justify-center overflow-hidden transition-all group flex-shrink-0"
              style={groupAvatarPreview ? { boxShadow: '0 0 24px rgba(0,229,255,0.2)', borderStyle: 'solid', borderColor: 'rgba(0,229,255,0.35)' } : {}}
              data-testid="group-avatar-upload">
              {groupAvatarPreview ? (
                <img src={groupAvatarPreview} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-6 h-6 text-white/40 group-hover:text-cyan-400 transition-colors" />
              )}
            </button>
            <div className="flex-1">
              <JewelField label="Group name" required>
                <JewelInput value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Q4 Launch Room…" autoFocus />
              </JewelField>
            </div>
          </div>

          <JewelField label={`Members (${selectedGroupMembers.length} selected)`} required>
            <div className="max-h-56 overflow-y-auto space-y-1 bg-white/[0.02] border border-white/[0.06] rounded-xl p-2 jewel-scroll">
              {staffList.map(staff => {
                const active = selectedGroupMembers.includes(staff.username);
                const status = getStaffStatus(staff.username);
                return (
                  <button key={staff.username}
                    onClick={() => {
                      if (active) setSelectedGroupMembers(prev => prev.filter(u => u !== staff.username));
                      else setSelectedGroupMembers(prev => [...prev, staff.username]);
                    }}
                    data-testid={`group-member-${staff.username}`}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all border ${
                      active
                        ? 'bg-gradient-to-r from-cyan-500/15 to-purple-500/15 border-cyan-400/30'
                        : 'border-transparent hover:bg-white/[0.03] hover:border-white/[0.06]'
                    }`}>
                    <div className="relative w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {staff.avatar ? (
                        <img src={getImageUrl(staff.avatar)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-sm font-medium">{staff.display_name?.charAt(0)?.toUpperCase() || 'S'}</span>
                      )}
                      {status.online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0a0d1a]" />}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className={`text-sm truncate ${active ? 'text-white' : 'text-white/80'}`}>{staff.display_name || staff.username}</p>
                      <p className="text-white/40 text-[10px] tracking-wide capitalize">{staff.role || 'Staff'}</p>
                    </div>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-cyan-400' : 'border border-white/15'}`}>
                      {active && <Check className="w-2.5 h-2.5 text-[#05050A]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </JewelField>
        </div>
      </JewelModal>

      {/* Group Settings Modal — Jewel */}
      <JewelModal
        open={showGroupSettingsModal}
        onOpenChange={setShowGroupSettingsModal}
        icon={Settings}
        title="Group Settings"
        subtitle="Manage members, calls, and shared space"
        accent="cyan"
        size="md"
        testid="group-settings-modal"
        footer={<JewelButton variant="ghost" onClick={() => setShowGroupSettingsModal(false)}>Close</JewelButton>}
      >
        {(() => {
          const group = groups.find(g => g.id === activeChat);
          if (!group) return <p className="text-white/40 text-sm">Group not found.</p>;
          const isOwner = group.owner === currentUser?.username;
          return (
            <div className="space-y-4">
              {/* Header card */}
              <div className="relative p-4 rounded-2xl overflow-hidden border border-white/[0.06]"
                style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.06) 0%, rgba(138,43,226,0.06) 100%)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-purple-500/30 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {group.avatar ? <img src={getImageUrl(group.avatar)} alt="" className="w-full h-full object-cover" /> : <Users className="w-5 h-5 text-cyan-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{group.name}</p>
                    <p className="text-white/40 text-xs mt-0.5">{group.members?.length || 0} members</p>
                  </div>
                  {isOwner && <Badge className="bg-amber-500/10 border-amber-400/30 text-amber-300"><Crown className="w-3 h-3 mr-1" /> Owner</Badge>}
                </div>
              </div>

              {/* Quick actions */}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => { handleStartGroupCall(activeChat); setShowGroupSettingsModal(false); }}
                  data-testid="group-call-start"
                  className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-400/25 text-emerald-300 hover:bg-emerald-500/15 transition-all flex items-center gap-2 justify-center text-sm">
                  <Video className="w-4 h-4" /> Start call
                </button>
                <button onClick={() => { navigator.clipboard.writeText(group.name); toast.success('Group name copied'); }}
                  className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white/70 hover:text-white hover:border-white/[0.15] transition-all flex items-center gap-2 justify-center text-sm">
                  <Copy className="w-4 h-4" /> Copy name
                </button>
              </div>

              <JewelField label={`Members (${group.members?.length || 0})`}>
                <div className="space-y-1 max-h-56 overflow-y-auto pr-1 jewel-scroll">
                  {group.members?.map(username => {
                    const staff = getStaffInfo(username);
                    const isMemberOwner = group.owner === username;
                    const isCurrentUser = username === currentUser?.username;
                    const status = getStaffStatus(username);
                    return (
                      <div key={username} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] group hover:border-white/15 transition-all">
                        <div className="relative w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center overflow-hidden flex-shrink-0">
                          {staff.avatar ? <img src={getImageUrl(staff.avatar)} alt="" className="w-full h-full object-cover" /> : <span className="text-white text-sm">{staff.display_name?.charAt(0)?.toUpperCase() || 'S'}</span>}
                          {status.online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0a0d1a]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-white/90 text-sm">{staff.display_name || username}</span>
                          {isCurrentUser && <span className="text-white/40 text-[10px] ml-1.5">(You)</span>}
                        </div>
                        {isMemberOwner && <Crown className="w-3.5 h-3.5 text-amber-300" />}
                        {isOwner && !isMemberOwner && !isCurrentUser && (
                          <button onClick={() => handleRemoveMember(activeChat, username)}
                            data-testid={`group-remove-${username}`}
                            className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                            title="Remove"><UserMinus className="w-4 h-4" /></button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </JewelField>
            </div>
          );
        })()}
      </JewelModal>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* Bookmarks Panel */}
      {showBookmarks && (
        <ChatBookmarksPanel bookmarks={bookmarks} onClose={() => setShowBookmarks(false)}
          onNavigate={(chatId) => { setActiveChat(chatId); setActiveChatType(chatId.startsWith('group_') ? 'group' : ['general','operations','customer-relations','managers','announcements'].includes(chatId) ? 'channel' : 'dm'); setShowBookmarks(false); }}
          onRemove={(msgId) => { handleBookmarkToggle(msgId); }} />
      )}

      {/* Channel Info Panel */}
      {showChannelPanel && activeChatType === 'channel' && (
        <ChatChannelPanel channelInfo={channelInfo} pinnedMessages={pinnedMessages}
          onClose={() => setShowChannelPanel(false)} isManager={isManager}
          onSetTopic={handleSetChannelTopic}
          onUnpin={(msgId) => handlePinMessage(msgId)}
          channelName={getChatName()} />
      )}

      {/* Forward Modal */}
      <ChatForwardModal open={!!forwardingMessage} onClose={() => setForwardingMessage(null)}
        message={forwardingMessage} staffList={staffList} groups={groups}
        onForward={handleForwardMessage} />

      {/* Staff Profile Card */}
      <StaffProfileCard
        open={!!profileCardTarget}
        onClose={() => setProfileCardTarget(null)}
        targetUsername={profileCardTarget}
        getAuthHeaders={getAuthHeaders}
        onMessageClick={(username) => { setActiveChat(username); setActiveChatType('dm'); }}
      />
    </div>
  );
};

export default StaffChatPageRevamped;
